import { requireSupabase } from './supabase';

export function toLegacyPatient(row) {
  if (!row) return null;
  return {
    created_at: row.created_at,
    created_by: row.created_by,
    date_of_birth: row.date_of_birth,
    email: row.email,
    first_name: row.first_name,
    last_name: row.last_name,
    patient_id: row.id,
    patient_id_number: row.external_id || '',
    phone: row.phone,
    sex_at_birth: row.sex_at_birth,
    status: row.status,
  };
}

function generatePatientExternalId() {
  const raw =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '')
      : Math.random().toString(36).slice(2);
  return `PT-${raw.slice(0, 8).toUpperCase()}`;
}

function patientPayload(values) {
  return {
    date_of_birth: values.date_of_birth,
    email: values.email?.trim() || null,
    external_id: values.patient_id_number?.trim() || null,
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    phone: values.phone?.trim() || null,
    sex_at_birth: values.sex || 'not_recorded',
  };
}

export async function listPatients(organizationId) {
  const client = requireSupabase();
  const { data, error } = await client
    .from('patients')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .order('last_name', { ascending: true })
    .order('first_name', { ascending: true });

  if (error) throw error;
  return data.map(toLegacyPatient);
}

export async function createPatient(organizationId, userId, values) {
  const client = requireSupabase();
  const payload = patientPayload(values);
  // Auto-generate a patient identifier when one wasn't supplied.
  if (!payload.external_id) {
    payload.external_id = generatePatientExternalId();
  }
  const { data, error } = await client
    .from('patients')
    .insert({
      ...payload,
      created_by: userId,
      organization_id: organizationId,
    })
    .select('*')
    .single();

  if (error) throw error;
  return toLegacyPatient(data);
}

export async function updatePatient(organizationId, patientId, values) {
  const client = requireSupabase();
  const { data, error } = await client
    .from('patients')
    .update(patientPayload(values))
    .eq('organization_id', organizationId)
    .eq('id', patientId)
    .eq('status', 'active')
    .select('*')
    .single();

  if (error) throw error;
  return toLegacyPatient(data);
}

export async function archivePatient(organizationId, patientId) {
  const client = requireSupabase();
  const { error } = await client
    .from('patients')
    .update({ archived_at: new Date().toISOString(), status: 'archived' })
    .eq('organization_id', organizationId)
    .eq('id', patientId)
    .eq('status', 'active');

  if (error) throw error;
}

export async function getPatientHistory(organizationId, patientId) {
  const client = requireSupabase();
  const patientQuery = client
    .from('patients')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('id', patientId)
    .single();
  const assessmentQuery = client
    .from('age_assessments')
    .select('id, specimen_collection_at, chronological_age_years, pheno_age_years, bortz_age_years, pranagen_age_years, mortality_risk_10y, status, algorithm_version')
    .eq('organization_id', organizationId)
    .eq('patient_id', patientId)
    .order('specimen_collection_at', { ascending: true });
  const [patientResult, assessmentResult] = await Promise.all([
    patientQuery,
    assessmentQuery,
  ]);

  if (patientResult.error) throw patientResult.error;
  if (assessmentResult.error) throw assessmentResult.error;

  return {
    analyses: assessmentResult.data.map((assessment) => ({
      algorithm_version: assessment.algorithm_version,
      analysis_id: assessment.id,
      bortz_age: assessment.bortz_age_years,
      chronological_age: assessment.chronological_age_years,
      collection_date: assessment.specimen_collection_at,
      mortality_risk: assessment.mortality_risk_10y,
      pheno_age: assessment.pheno_age_years,
      pranagen_clock: assessment.pranagen_age_years,
      status: assessment.status,
    })),
    patient: toLegacyPatient(patientResult.data),
  };
}

export async function getDashboardStats(organizationId) {
  const client = requireSupabase();
  const patientCountQuery = client
    .from('patients')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('status', 'active');
  const assessmentCountQuery = client
    .from('age_assessments')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId);
  const recentQuery = client
    .from('age_assessments')
    .select('id, patient_id, specimen_collection_at, chronological_age_years, pranagen_age_years')
    .eq('organization_id', organizationId)
    .order('specimen_collection_at', { ascending: false })
    .limit(5);
  const [patientCountResult, assessmentCountResult, recentResult] = await Promise.all([
    patientCountQuery,
    assessmentCountQuery,
    recentQuery,
  ]);

  if (patientCountResult.error) throw patientCountResult.error;
  if (assessmentCountResult.error) throw assessmentCountResult.error;
  if (recentResult.error) throw recentResult.error;

  const patientIds = [...new Set(recentResult.data.map((item) => item.patient_id))];
  let patientMap = new Map();
  if (patientIds.length) {
    const { data, error } = await client
      .from('patients')
      .select('id, first_name, last_name')
      .eq('organization_id', organizationId)
      .in('id', patientIds);
    if (error) throw error;
    patientMap = new Map(data.map((patient) => [patient.id, patient]));
  }

  return {
    analysis_count: assessmentCountResult.count || 0,
    patient_count: patientCountResult.count || 0,
    recent_analyses: recentResult.data.map((assessment) => {
      const patient = patientMap.get(assessment.patient_id);
      return {
        analysis_id: assessment.id,
        chronological_age: assessment.chronological_age_years,
        collection_date: assessment.specimen_collection_at,
        patient_name: patient
          ? `${patient.first_name} ${patient.last_name}`
          : 'Unknown Patient',
        pranagen_clock: assessment.pranagen_age_years,
      };
    }),
  };
}
